import React, { useState, useEffect } from 'react';
import { Search, Play, Heart } from 'lucide-react';
import Styles from "../styles/Sermons.module.css";
import heroImg from "../assets/images/image2.png";
import EventNotificationPopup from "../components/EventNotificationPopup";

const Sermons = () => {
  const [sermons, setSermons] = useState([]);
  const [viewMode, setViewMode] = useState('grid'); 
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedSeries, setSelectedSeries] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [activeTab, setActiveTab] = useState('all');
  const [favorites, setFavorites] = useState([]);
  const [sortBy, setSortBy] = useState('newest');
  const sermonsPerPage = 9;

  useEffect(() => {
    const mockSermons = [
      {
        id: 1,
        title: "Taking Authority Over Your Spiritual Climate",
        preacher: "Apostle Sebagabo Christophe",
        date: "2024-01-15",
        series: "Rule Your Atmosphere",
        type: "rule-your-atmosphere",
        youtubeId: "a2pzRy06NIU",
      },
      {
        id: 2,
        title: "Creating a Heaven on Earth Environment",
        preacher: "Apostle Sebagabo Christophe",
        date: "2024-01-08",
        series: "Rule Your Atmosphere",
        type: "rule-your-atmosphere",
        youtubeId: "jbJw9bj3eQA",
      },
      {
        id: 3,
        title: "Breaking Negative Spiritual Patterns",
        preacher: "Apostle Sebagabo Christophe",
        date: "2024-01-01",
        series: "Rule Your Atmosphere",
        type: "rule-your-atmosphere",
        youtubeId: "OSbu44vw2ew",
      },
      {
        id: 4,
        title: "The Power of Faith in Difficult Times",
        preacher: "Apostle Sebagabo Christophe",
        date: "2024-01-14",
        series: "Faith Journey",
        type: "sunday-services",
        youtubeId: "bUiXASzdfo8",
      },
      {
        id: 5,
        title: "Love Your Neighbor as Yourself",
        preacher: "Apostle Sebagabo Christophe",
        date: "2024-01-07",
        series: "The Great Commandment",
        type: "sunday-services",
        youtubeId: "a2pzRy06NIU",
      },
      {
        id: 6,
        title: "Finding Peace in the Storm",
        preacher: "Apostle Sebagabo Christophe",
        date: "2023-12-31",
        series: "Peace in Chaos",
        type: "sunday-services",
        youtubeId: "jbJw9bj3eQA",
      },
      {
        id: 7,
        title: "The Gift of Grace",
        preacher: "Apostle Sebagabo Christophe",
        date: "2023-12-24",
        series: "Christmas Series",
        type: "sunday-services",
        youtubeId: "OSbu44vw2ew",
      },
      {
        id: 8,
        title: "Walking in Purpose",
        preacher: "Apostle Sebagabo Christophe",
        date: "2023-12-17",
        series: "Divine Purpose",
        type: "sunday-services",
        youtubeId: "bUiXASzdfo8",
      },
      {
        id: 9,
        title: "The Power of Prayer",
        preacher: "Apostle Sebagabo Christophe",
        date: "2023-12-10",
        series: "Spiritual Disciplines",
        type: "sunday-services",
        youtubeId: "a2pzRy06NIU",
      }
    ];
    setSermons(mockSermons);
  }, []);

  const sortedSermons = [...sermons].sort((a, b) => {
    switch(sortBy) {
      case 'newest':
        return new Date(b.date) - new Date(a.date);
      case 'oldest':
        return new Date(a.date) - new Date(b.date);
      default:
        return 0;
    }
  });

  const filteredSermons = sortedSermons.filter(sermon => {
    const matchesSearch = sermon.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         sermon.preacher.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesSeries = selectedSeries === 'all' || sermon.series === selectedSeries;
    const matchesTab = activeTab === 'all' || sermon.type === activeTab;

    return matchesSearch && matchesSeries && matchesTab;
  });

  const seriesOptions = ['all', ...new Set(
    sortedSermons
      .filter(sermon => activeTab === 'all' || sermon.type === activeTab)
      .map(sermon => sermon.series)
  )];

  const indexOfLastSermon = currentPage * sermonsPerPage;
  const indexOfFirstSermon = indexOfLastSermon - sermonsPerPage;
  const currentSermons = filteredSermons.slice(indexOfFirstSermon, indexOfLastSermon);
  const totalPages = Math.ceil(filteredSermons.length / sermonsPerPage);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const toggleFavorite = (sermonId) => {
    setFavorites(prev => 
      prev.includes(sermonId) 
        ? prev.filter(id => id !== sermonId)
        : [...prev, sermonId]
    );
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setSelectedSeries('all');
    setCurrentPage(1);
  };

  const getYoutubeEmbedUrl = (youtubeId) => {
    return `https://www.youtube.com/embed/${youtubeId}?rel=0`;
  };

  const PaginationControls = () => {
    const getPageNumbers = () => {
      const pages = [];
      const maxVisible = 7; // Maximum number of page buttons to show
      
      if (totalPages <= maxVisible) {
        // Show all pages if total is less than max
        for (let i = 1; i <= totalPages; i++) {
          pages.push(i);
        }
      } else {
        // Always show first page
        pages.push(1);
        
        let startPage, endPage;
        
        if (currentPage <= 3) {
          // Near the beginning
          startPage = 2;
          endPage = 5;
        } else if (currentPage >= totalPages - 2) {
          // Near the end
          startPage = totalPages - 4;
          endPage = totalPages - 1;
        } else {
          // In the middle
          startPage = currentPage - 1;
          endPage = currentPage + 1;
        }
        
        // Add ellipsis after first page if needed
        if (startPage > 2) {
          pages.push('...');
        }
        
        // Add middle pages
        for (let i = startPage; i <= endPage; i++) {
          pages.push(i);
        }
        
        // Add ellipsis before last page if needed
        if (endPage < totalPages - 1) {
          pages.push('...');
        }
        
        // Always show last page
        pages.push(totalPages);
      }
      
      return pages;
    };

    return (
      <div className={Styles.pagination}>
        <button
          className={Styles.paginationButton}
          onClick={() => setCurrentPage(1)}
          disabled={currentPage === 1}
          title="First page"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M11 17l-5-5 5-5M18 17l-5-5 5-5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
        
        <button
          className={Styles.paginationButton}
          onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
          disabled={currentPage === 1}
          title="Previous page"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M15 18l-6-6 6-6" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
        
        {getPageNumbers().map((page, index) => (
          page === '...' ? (
            <span key={`ellipsis-${index}`} className={Styles.paginationEllipsis}>
              ...
            </span>
          ) : (
            <button
              key={page}
              className={`${Styles.paginationButton} ${currentPage === page ? Styles.active : ''}`}
              onClick={() => setCurrentPage(page)}
            >
              {page}
            </button>
          )
        ))}
        
        <button
          className={Styles.paginationButton}
          onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
          disabled={currentPage === totalPages}
          title="Next page"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M9 18l6-6-6-6" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
        
        <button
          className={Styles.paginationButton}
          onClick={() => setCurrentPage(totalPages)}
          disabled={currentPage === totalPages}
          title="Last page"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M13 17l5-5-5-5M6 17l5-5-5-5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
      </div>
    );
  };

  return (
    <div className={Styles.sermonsPage}>
      {/* Event Notification Popup */}
      <EventNotificationPopup />

      {/* Hero Section */}
      <section 
        className={Styles.heroSection}
        style={{ '--hero-image': `url(${heroImg})` }}
      >
        <div className={Styles.heroContent}>
          <h1 className={Styles.heroTitle}>Our Sermons</h1>
          <p className={Styles.heroSubtitle}>
            Explore our collection of inspiring messages from Sunday Services and Rule Your Atmosphere teachings. Find messages that inspire and transform your life.
          </p>
        </div>
      </section>

      {/* Sermon Type Tabs */}
      <section className={Styles.tabsSection}>
        <div className={Styles.tabsContainer}>
          <button
            className={`${Styles.tabButton} ${activeTab === 'all' ? Styles.tabActive : ''}`}
            onClick={() => handleTabChange('all')}
          >
            All Sermons
          </button>
          <button
            className={`${Styles.tabButton} ${activeTab === 'sunday-services' ? Styles.tabActive : ''}`}
            onClick={() => handleTabChange('sunday-services')}
          >
            Sunday Services
          </button>
          <button
            className={`${Styles.tabButton} ${activeTab === 'rule-your-atmosphere' ? Styles.tabActive : ''}`}
            onClick={() => handleTabChange('rule-your-atmosphere')}
          >
            Rule Your Atmosphere
          </button>
        </div>
      </section>

      {/* Filters Section */}
      <section className={Styles.filtersSection}>
        <div className={Styles.filtersContent}>
          <div className={Styles.filtersRow}>
            <div className={Styles.searchBox}>
              <Search className={Styles.searchIcon} size={18} />
              <input
                type="text"
                placeholder="Search by title or preacher..."
                className={Styles.searchInput}
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1);
                }}
              />
            </div>

            <div className={Styles.filterGroup}>
              <select
                className={Styles.filterSelect}
                value={selectedSeries}
                onChange={(e) => {
                  setSelectedSeries(e.target.value);
                  setCurrentPage(1);
                }}
              >
                <option value="all">All Series</option>
                {seriesOptions.filter(series => series !== 'all').map(series => (
                  <option key={series} value={series}>{series}</option>
                ))}
              </select>

              <select
                className={Styles.filterSelect}
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
              >
                <option value="newest">Newest First</option>
                <option value="oldest">Oldest First</option>
              </select>
            </div>
          </div>
        </div>
      </section>

      {/* Sermons Section */}
      <section className={Styles.sermonsSection}>
        {currentSermons.length === 0 ? (
          <div className={Styles.emptyState}>
            <div className={Styles.emptyStateIcon}>🎵</div>
            <h2 className={Styles.emptyStateTitle}>No Sermons Found</h2>
            <p className={Styles.emptyStateText}>
              Try adjusting your search terms or filters to find what you're looking for.
            </p>
          </div>
        ) : (
          <>
            {/* Top Pagination */}
            {totalPages > 1 && <PaginationControls />}

            <div className={Styles.sermonsGrid}>
              {currentSermons.map(sermon => (
                <div
                  key={sermon.id}
                  className={Styles.sermonCard}
                >
                  <div className={Styles.videoContainer}>
                    <iframe
                      src={getYoutubeEmbedUrl(sermon.youtubeId)}
                      title={sermon.title}
                      className={Styles.videoFrame}
                      allowFullScreen
                      loading="lazy"
                    ></iframe>
                    <button 
                      className={`${Styles.favoriteButton} ${favorites.includes(sermon.id) ? Styles.active : ''}`}
                      onClick={() => toggleFavorite(sermon.id)}
                      title="Add to favorites"
                    >
                      <Heart size={18} fill={favorites.includes(sermon.id) ? "currentColor" : "none"} />
                    </button>
                  </div>
                  
                  <div className={Styles.sermonContent}>
                    <a 
                      href={`https://www.youtube.com/watch?v=${sermon.youtubeId}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={Styles.watchButton}
                    >
                      <Play size={18} />
                      <span>Watch on YouTube</span>
                    </a>
                  </div>
                </div>
              ))}
            </div>

            {/* Bottom Navigation Buttons */}
            {totalPages > 1 && (
              <div className={Styles.paginationBottom}>
                <button
                  className={Styles.paginationNavButton}
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M15 18l-6-6 6-6" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  <span>Previous</span>
                </button>
                
                <button
                  className={Styles.paginationNavButton}
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                  disabled={currentPage === totalPages}
                >
                  <span>Next</span>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M9 18l6-6-6-6" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
              </div>
            )}
          </>
        )}
      </section>
    </div>
  );
};

export default Sermons;